import type { TFunction } from 'i18next';
import type {
  CustomizationInput,
  CustomizedCategory,
  Frequency,
  ParticipantRole,
  PlanCard,
  PlanDetail,
  PlanInput,
  PlanOptions,
  PlanStatus,
} from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';

export const planPath = (id: number) => Routes.SafetyRepRoundPlan.replace(':planId', String(id));

/** The general manager opens a plan from their own overview, and comes back there */
export const managerPlanPath = (id: number) => Routes.GeneralManagerPlan.replace(':planId', String(id));

/** Where the plans are listed for the reader: the general manager's overview, or the safety representative's plans */
export const plansHomeFor = (pathname: string) =>
  pathname.startsWith('/daglig-leder/') ? Routes.GeneralManagerSafetyRoundOverview : Routes.SafetyRepRoundPlans;
/** The API refused the safety representative, who does not have the SAFETY_REP role */
export const isSafetyRepWithoutRole = (error: unknown) =>
  (error as { response?: { data?: { code?: string } } } | null)?.response?.data?.code === 'safetyRepWithoutRole';

export const planChecklistPath = (id: number) => Routes.SafetyRepRoundPlanChecklist.replace(':planId', String(id));

export const STATUS_COLORS: Record<PlanStatus, 'success' | 'warning' | 'danger' | 'info'> = {
  onTrack: 'success',
  soon: 'warning',
  overdue: 'danger',
  awaitingSignature: 'info',
  sentBack: 'warning',
};

export type RoundAction =
  | { kind: 'protocol'; roundId: number }
  | { kind: 'correct'; roundId: number }
  | { kind: 'continue'; roundId: number }
  | { kind: 'start' }
  | { kind: 'notYet'; startsFrom: string };

/**
 * A round waiting for the general manager is walked, so its protocol opens instead of a new round. A protocol sent back
 * is corrected from the protocol, which says what is asked. A new round starts from its notice period.
 */
export const roundActionOf = (
  plan: Pick<PlanCard, 'status' | 'currentRoundId' | 'canStart' | 'startsFrom'>,
): RoundAction => {
  if (plan.currentRoundId === null)
    return plan.canStart ? { kind: 'start' } : { kind: 'notYet', startsFrom: plan.startsFrom };
  if (plan.status === 'awaitingSignature') return { kind: 'protocol', roundId: plan.currentRoundId };
  if (plan.status === 'sentBack') return { kind: 'correct', roundId: plan.currentRoundId };
  return { kind: 'continue', roundId: plan.currentRoundId };
};

/** «10. april 2027» */
export const longDate = (day: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${day.slice(0, 10)}T00:00:00Z`));

/** «2. okt. 2026», or «2. okt.» without the year */
export const shortDate = (day: string, language: string, withYear = true) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(new Date(`${day.slice(0, 10)}T00:00:00Z`));

/** The plan whose round comes first */
/** The round to walk next. A plan whose protocol waits for the general manager has been walked. */
export const nextPlan = (plans: PlanCard[]) =>
  plans
    .filter((plan) => plan.status !== 'awaitingSignature')
    .sort((a, b) => a.nextDate.localeCompare(b.nextDate) || a.id - b.id)[0] ?? null;

/** «Daglig leder, verneombud og leder» */
export const participantsText = (roles: ParticipantRole[], t: TFunction) => {
  const names = roles.map((role) => t(`safetyRounds.plans.roles.${role}`));
  const lower = names.map((name, index) => (index === 0 ? name : name.toLowerCase()));
  return lower.length <= 1 ? (lower[0] ?? '') : `${lower.slice(0, -1).join(', ')} ${t('safetyRounds.and')} ${lower[lower.length - 1]}`;
};

export const FREQUENCY_OPTIONS: Frequency[] = ['ANNUAL', 'SEMIANNUAL', 'QUARTERLY', 'MONTHLY'];

export type PlanForm = {
  name: string;
  area: string;
  templateId: number | null;
  frequency: Frequency;
  nextDate: string;
  noticeDays: number;
  participants: ParticipantRole[];
  safetyRepId: number | null;
};

export const newPlanForm = (options: Pick<PlanOptions, 'lockedParticipants'>): PlanForm => ({
  name: '',
  area: '',
  templateId: null,
  frequency: 'ANNUAL',
  nextDate: '',
  noticeDays: 30,
  participants: [...options.lockedParticipants],
  safetyRepId: null,
});

export const planToForm = (plan: PlanDetail): PlanForm => ({
  name: plan.name,
  area: plan.area,
  templateId: plan.templateId,
  frequency: plan.frequency,
  nextDate: plan.nextDate,
  noticeDays: plan.noticeDays,
  participants: plan.participants,
  safetyRepId: plan.safetyRepId,
});

export const formToInput = (form: PlanForm, choosesSafetyRep: boolean): PlanInput => ({
  name: form.name,
  area: form.area,
  templateId: form.templateId,
  frequency: form.frequency,
  nextDate: form.nextDate,
  noticeDays: form.noticeDays,
  participants: form.participants,
  ...(choosesSafetyRep ? { safetyRepId: form.safetyRepId } : {}),
});

export const PLAN_FIELD_IDS = {
  name: 'plan-name',
  area: 'plan-area',
  template: 'plan-template',
  nextDate: 'plan-next-date',
  safetyRep: 'plan-safety-rep',
};

export type PlanFormError = { fieldId: string; message: string };

/** Checked here for instant feedback, in form order. The API checks again. */
export const planFormErrors = (
  form: PlanForm,
  choosesSafetyRep: boolean,
  t: TFunction,
  /** Those with the SAFETY_REP role, who can be chosen. A plan may still have someone who has lost the role. */
  safetyRepIds?: number[],
): PlanFormError[] => {
  const K = 'safetyRounds.plan.errors';
  return [
    ...(form.name.trim() ? [] : [{ fieldId: PLAN_FIELD_IDS.name, message: t(`${K}.name`) }]),
    ...(form.area.trim() ? [] : [{ fieldId: PLAN_FIELD_IDS.area, message: t(`${K}.area`) }]),
    ...(form.templateId ? [] : [{ fieldId: PLAN_FIELD_IDS.template, message: t(`${K}.template`) }]),
    ...(/^\d{4}-\d{2}-\d{2}$/.test(form.nextDate) ? [] : [{ fieldId: PLAN_FIELD_IDS.nextDate, message: t(`${K}.nextDate`) }]),
    ...(!choosesSafetyRep || form.safetyRepId ? [] : [{ fieldId: PLAN_FIELD_IDS.safetyRep, message: t(`${K}.safetyRep`) }]),
    ...(choosesSafetyRep && form.safetyRepId && safetyRepIds && !safetyRepIds.includes(form.safetyRepId)
      ? [{ fieldId: PLAN_FIELD_IDS.safetyRep, message: t(`${K}.safetyRepRole`) }]
      : []),
  ];
};

export const toggleParticipant = (form: PlanForm, role: ParticipantRole, locked: ParticipantRole[]): PlanForm =>
  locked.includes(role)
    ? form
    : {
        ...form,
        participants: form.participants.includes(role)
          ? form.participants.filter((candidate) => candidate !== role)
          : [...form.participants, role],
      };

/** A checkpoint added for the area while the safety representative edits, before it is saved */
export type DraftExtra = { id: string; categoryKey: string; text: string };

export type CustomizationForm = { hidden: string[]; extra: DraftExtra[] };

let counter = 0;
const localId = () => `extra-${(counter += 1)}`;

export const toCustomizationForm = (checklist: CustomizedCategory[]): CustomizationForm => ({
  hidden: checklist.flatMap((category) =>
    category.checkpoints.filter((point) => point.hidden && point.key).map((point) => point.key as string),
  ),
  extra: checklist.flatMap((category) =>
    category.checkpoints
      .filter((point) => point.source === 'PLAN')
      .map((point) => ({ id: `saved-${point.extraId ?? localId()}`, categoryKey: category.key, text: point.text })),
  ),
});

export const toggleHidden = (form: CustomizationForm, key: string): CustomizationForm => ({
  ...form,
  hidden: form.hidden.includes(key) ? form.hidden.filter((candidate) => candidate !== key) : [...form.hidden, key],
});

export const addExtra = (form: CustomizationForm, categoryKey: string, text: string): CustomizationForm =>
  text.trim() ? { ...form, extra: [...form.extra, { id: localId(), categoryKey, text: text.trim() }] } : form;

export const removeExtra = (form: CustomizationForm, id: string): CustomizationForm => ({
  ...form,
  extra: form.extra.filter((point) => point.id !== id),
});

export const customizationInput = (form: CustomizationForm): CustomizationInput => ({
  hiddenKeys: form.hidden,
  extra: form.extra.map(({ categoryKey, text }) => ({ categoryKey, text })),
});

/** How many checkpoints the round will have with the choices made */
export const includedCount = (checklist: CustomizedCategory[], form: CustomizationForm) =>
  checklist.reduce(
    (sum, category) =>
      sum +
      category.checkpoints.filter((point) => point.source === 'TEMPLATE' && point.key && !form.hidden.includes(point.key))
        .length,
    0,
  ) + form.extra.length;
